import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { PaginateModel } from "../../../models/PaginateModel";
import { PaginationLength } from "../../../../_metronic/partials/pagination/PaginationLength";
import { Paginate } from "../../../../_metronic/partials/pagination/Paginate";
import { DEFAULT_PER_PAGE } from "../../../constants/common";
import ConfigModal from "../../../components/Modal/ConfigModal";
import { HeaderModel, ModalModel } from "../../../models/CommonModel";
import PayrollAccountTable from "../components/PayrollAccountTable";
import { PayrollModel, PayrollRowModel, PayrollRowSearchModel } from "../../../models/account-books/PayrollAccount";
import { createPayroll, exportPayrollBooks, getPayrollBooks, updatePayroll } from "../AccountBooksCRUD";
import { Toast, download } from "../../../modules/common/helper";
import moment from "moment";
import PayrollPaymentModal from "../components/PayrollPaymentModal";
import { formatPrice } from "../../../selectors";

interface Props {
    year: string
    opening?: string
    closing?: string
    changeTab: (tab: string) => void
    isFullYear: boolean
}

const PayrollAccount: FC<Props> = ({year, opening, closing, changeTab, isFullYear}) => {
    const initMonth = moment().month();
    const initSearch = {
        page: 1,
        per_page: DEFAULT_PER_PAGE
    }
    const [search, setSearch] = useState<PayrollRowSearchModel>(initSearch)
    const [paginateData, setPagData] = useState<PaginateModel<PayrollRowModel>>()
    const [data, setData] = useState<PayrollRowModel[]>([])
    const [month, setMonth] = useState<number>(initMonth)
    const [defaultWorkDays, setDefaultWorkDays] = useState<number>(0)
    const [actualReceived, setActualReceived] = useState<number>(0)
    const [insurance, setInsurance] = useState<{
        social_insurance: number,
        health_insurance: number,
        unemployment_insurance: number
    }>({
        social_insurance: 0,
        health_insurance: 0,
        unemployment_insurance: 0
    })
    const [paid, setPaid] = useState<{
        salary?: number
        s_insurance?: number,
        h_insurance?: number,
        u_insurance?: number
    }>()
    const [configModal, setConfigModal] = useState<ModalModel>({
        show: false,
        mode: 'add',
    });
    const [paymentModal, setPaymentModal] = useState<ModalModel>({
        show: false,
        mode: 'add',
    });
    const [hideCols, setHideCols] = useState<string[]>([]);
    const header: HeaderModel[] = [
        {
            label: 'Họ và tên',
            field: 'name',
            style: { maxWidth: "150px", minWidth: "150px", },
            sortable: false
        },
        {
            label: 'Số công',
            field: 'work_days',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: false
        },
        {
            label: 'Lương ngày',
            field: 'cost',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: false
        },
        {
            label: 'Tiền lương',
            field: 'salary',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: false
        },
        {
            label: 'BHXH',
            field: 'social_insurance',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: false
        },
        {
            label: 'BHTN',
            field: 'unemployment_insurance',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: false
        },
        {
            label: 'BHYT',
            field: 'health_insurance',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: false
        },
        {
            label: 'Thực nhận',
            field: 'actual_received',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: false
        }
    ]

    const handleChangeVisCols = (e: any) => {
        let hiddenCols = [...hideCols];
        if (!e.target?.checked) {
            setHideCols([...hiddenCols, e.target.name])
        } else {
            setHideCols(hiddenCols.filter((item) => item !== e.target.name))
        }
    }

    useEffect(() => {
        if (year) {
            let startDate = moment({ year: parseInt(year), month }).startOf('month').format('YYYY-MM-DD')
            let endDate   = moment({ year: parseInt(year), month }).endOf('month').format('YYYY-MM-DD')
            getData({...initSearch, start_date: startDate, end_date: endDate});
        }
    }, [year])

    const getData = async (searchParams: PayrollRowSearchModel) => {
        try {
            const { data: resData } = await getPayrollBooks(searchParams);
            setData(resData.paginate_data?.data ?? []);
            setPagData(resData.paginate_data);
            setActualReceived(resData.sub_total.actual_received ?? 0);
            setInsurance({
                social_insurance: resData.sub_total.social_insurance ?? 0,
                health_insurance: resData.sub_total.health_insurance ?? 0,
                unemployment_insurance: resData.sub_total.unemployment_insurance ?? 0
            });
            setPaid(resData.paid);
            setSearch(searchParams);
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }

    const handleChangeMonth = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        let selectedMonth = parseInt(e.target.value);
        let startDate = moment({ year: parseInt(year), month: selectedMonth }).startOf('month').format('YYYY-MM-DD')
        let endDate   = moment({ year: parseInt(year), month: selectedMonth }).endOf('month').format('YYYY-MM-DD')
        setMonth(selectedMonth);
        getData({...initSearch, start_date: startDate, end_date: endDate});
    }

    const handleSearch = () => {

    }

    const handlePageLengthChange = (e: any) => {
        getData({ ...search, page: 1, per_page: +e.target.value })
    }

    const handlePageChange = (data: any) => {
        getData({ ...search, page: data.selected + 1 })
    }

    const handleSort = (sortBy: string, direction: 'asc' | 'desc' | 'none') => {
        const currentSearch = { ...search };
        const sortParams: PayrollRowSearchModel = { ...currentSearch, sort_by: sortBy, sort_direction: direction };
        if (direction === 'none') {
            delete sortParams.sort_by;
            delete sortParams.sort_direction;
        }
        getData({ ...sortParams });
    }

    const handleExport = async () => {
        const { data } = await exportPayrollBooks({ ...search });
        download(data, '11-So thanh toan luong');
    }

    const handleSetWorkDays = (e: ChangeEvent<HTMLInputElement>, id: number) => {
        let curData = [...data ?? []];
        let findIndex = curData.findIndex((el: PayrollRowModel) => el.id === id);
        if (findIndex >= 0) {
            let curPayroll = curData[findIndex].cur_payroll ?? {};
            curData[findIndex] = {
                ...curData[findIndex],
                cur_payroll: updatePayrollData({
                    ...curPayroll, 
                    work_days: parseInt(e.target.value),
                    cost: curData[findIndex].day_cost
                })
            }
            setData(curData)
        }
    }

    const handleSetInsurance = (id: number, value: number, type: string) => {
        let curData = [...data ?? []];
        let findIndex = curData.findIndex((el: PayrollRowModel) => el.id === id);
        if (findIndex >= 0) {
            let curPayroll = curData[findIndex].cur_payroll ?? {};
            curData[findIndex] = {
                ...curData[findIndex],
                cur_payroll: updatePayrollData({
                    ...curPayroll, 
                    [type]: value
                })
            }
            setData(curData)
        }
    }

    const updatePayrollData = (cur_payroll: PayrollModel) => {
        let newSalary = (cur_payroll.cost ?? 0) * (cur_payroll.work_days ?? 0);
        let sInsurance = cur_payroll.social_insurance ?? 0
        let hInsurance = cur_payroll.health_insurance ?? 0
        let uInsurance = cur_payroll.unemployment_insurance ?? 0;
        let insurance = sInsurance + hInsurance + uInsurance;
        return {
            ...cur_payroll,
            salary: newSalary,
            actual_received: newSalary - insurance
        }
    }

    const handleUpdate = async (id: number) => {
        let monthData = moment({ year: parseInt(year), month }).startOf('month')
        let rowData = data?.find((el: PayrollRowModel) => el.id === id);
        if (!rowData) { return }
        if (!rowData.cur_payroll?.work_days || rowData.cur_payroll?.work_days == 0) {
            Toast.fire({
                icon: 'error',
                title: 'Vui lòng nhập ngày công!'
            })
            return
        }
        try {
            await updatePayroll(rowData, monthData.format('YYYY-MM-DD'));
            Toast.fire({
                icon: 'success',
                title: 'Cập nhật thành công!'
            })
            getData({...search});
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: e.response?.data?.message ?? 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }

    const handleAddPayment = async (values: any) => {
        let monthData = moment({ year: parseInt(year), month }).startOf('month')
        try {
            await createPayroll(values, monthData.format('YYYY-MM-DD'));
            getData({...search});
            Toast.fire({
                icon: 'success',
                title: 'Tạo phiếu chi lương thành công!'
            })
            setPaymentModal({...paymentModal, show: false})
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: e.response?.data?.message ?? 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }

    const inSurance = useMemo(function () {
        return insurance.health_insurance 
            + insurance.social_insurance 
            + insurance.unemployment_insurance;
    }, [insurance]);

    const paidAmount = useMemo(function () {
        return (paid?.salary ?? 0)
            + (paid?.h_insurance ?? 0)
            + (paid?.s_insurance ?? 0)
            + (paid?.u_insurance ?? 0);
    }, [paid])

    const getMonthOptions = (start?: string, end?: string, yearStr?: string) => {
        const options: Array<{ value: number; label: string }> = [];
        const year = yearStr ? parseInt(yearStr, 10) : moment().year();
        if (!start || !end) {
            for (let i = 0; i < 12; i++) {
                options.push({ value: i, label: `Tháng ${i + 1}` });
            }
            return options;
        }

        const startMoment = moment(start, 'YYYY-MM-DD').startOf('month');
        const endMoment = moment(end, 'YYYY-MM-DD').endOf('month');

        const cursor = startMoment.clone();
        while (cursor.isSameOrBefore(endMoment, 'month')) {
            if (cursor.year() === year) {
                options.push({ value: cursor.month(), label: `Tháng ${cursor.month() + 1}` });
            }
            cursor.add(1, 'month');
        }

        if (options.length === 0) {
            for (let i = 0; i < 12; i++) {
                options.push({ value: i, label: `Tháng ${i + 1}` });
            }
        }
        return options;
    };

    const monthOptions = useMemo(() => getMonthOptions(opening, closing, year), [opening, closing, year]);

    useEffect(() => {
        if (monthOptions.length === 0) return;
        const firstMonth = monthOptions[0].value;
        const monthInOptions = monthOptions.some(opt => opt.value === month);
        if (!monthInOptions) {
            setMonth(firstMonth);
            const startDate = moment({ year: parseInt(year), month: firstMonth }).startOf('month').format('YYYY-MM-DD');
            const endDate = moment({ year: parseInt(year), month: firstMonth }).endOf('month').format('YYYY-MM-DD');
            getData({ ...initSearch, start_date: startDate, end_date: endDate });
        }
    }, [monthOptions]);

    return <>
    <div className='card mt-3'>
      <div className="card-header card-header-stretch">
        <h3 className="card-title">
            <button type="button" className="btn btn-primary btn-sm mx-1 mb-1 me-2" onClick={() => changeTab('index')}>
                <i className="fas fa-angle-left"/> 
            </button>
            Sổ lương
        </h3>
        <div className="d-flex align-items-center me-auto">
        <select
            className="form-select"
            value={month}
            onChange={handleChangeMonth}
            name="status"
        >
            {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
        </div>
        <div className="card-toolbar align-items-center">
            <div className="btn-groups">
                {actualReceived + inSurance - paidAmount > 0 && (
                    <button className="btn ms-auto me-1 btn-sm btn-primary text-hover-white" type="button"
                        onClick={() => setPaymentModal({...paymentModal, show: true, data: {
                            salary: actualReceived - (paid?.salary ?? 0),
                            social_insurance: insurance.social_insurance - (paid?.s_insurance ?? 0),
                            health_insurance: insurance.health_insurance - (paid?.h_insurance ?? 0),
                            unemployment_insurance: insurance.unemployment_insurance - (paid?.u_insurance ?? 0)
                        }})}>
                        Tạo phiếu chi
                    </button>
                )}
                <button className="btn mx-1 btn-sm btn-primary text-hover-white" type="button"
                    onClick={handleExport}>
                    Tải xuống
                </button>
            </div>
        </div>
      </div>
      <div className='card-body'>
        <div className="justify-content-between align-items-center my-3 row">
            <div className='col-12 col-lg-3'>
                <h3>Phải trả: <span className="text-bold">{formatPrice(actualReceived + inSurance)}</span></h3>
            </div>
            <div className='col-12 col-lg-3'>
                <h3>BH: <span className="text-bold">{formatPrice(inSurance)}</span></h3>
            </div>
            <div className='col-12 col-lg-3'>
                <h3>Đã trả: <span className="text-bold">{formatPrice(paidAmount)}</span></h3>
            </div>
            <div className='col-12 col-lg-3'>
                <h3>Còn lại: <span className="text-bold">{formatPrice(actualReceived + inSurance - paidAmount)}</span></h3>
            </div>
        </div>
        <PayrollAccountTable
          month={month}
          defaultWorkDays={defaultWorkDays}
          handleSetWorkDays={handleSetWorkDays}
          handleSetInsurance={handleSetInsurance}
          handleUpdate={handleUpdate}
          data={data || []}
          openColConfig={() => setConfigModal({ ...configModal, show: true })}
          header={header}
          hideCols={hideCols}
          handleSort={handleSort}
        />
      </div>
      <div className='card-footer d-flex justify-content-between paginate-wrapper'>
        <PaginationLength selected={search.per_page || DEFAULT_PER_PAGE} onChange={handlePageLengthChange} />
        <Paginate
            pageCount={paginateData?.meta?.last_page || 0}
            onPageChange={handlePageChange}
            forcePage={(search.page || 1) - 1}
        />
      </div>
      <ConfigModal
        {...configModal}
        header={header}
        hideCols={hideCols}
        handleChangeVisCols={handleChangeVisCols}
        onClose={() => { setConfigModal({ ...configModal, show: false }) }}
      />
      <PayrollPaymentModal
        {...paymentModal}
        onClose={() => { setPaymentModal({ ...paymentModal, show: false }) }}
        onSubmit={handleAddPayment}
        month={(month + 1)+'/'+year}
      />
    </div>
  </>
}

export default PayrollAccount;